/**
 * TaskCalendar — month-grid view of tasks by their `due_at`.
 *
 * Pure SVG-free DOM implementation (CSS Grid). One cell per day; tasks
 * appear as colour-coded chips inside their due cell. Clicking a chip calls
 * `onSelect`.
 *
 * No external library. Light theme only.
 */
import { useMemo, useState } from "react";

import { isOverdue, priorityChipClass, statusChipClass } from "./format";
import { STATUS_LABELS_JA } from "./types";
import type { Task } from "./types";

export interface TaskCalendarProps {
  tasks: Task[];
  onSelect?: (task: Task) => void;
  /** Defaults to today. Used to pick the visible month. */
  initialMonth?: Date;
}

const WEEKDAYS_JA = ["日", "月", "火", "水", "木", "金", "土"] as const;

export function TaskCalendar({
  tasks,
  onSelect,
  initialMonth,
}: TaskCalendarProps): JSX.Element {
  const [cursor, setCursor] = useState<Date>(() => {
    const d = initialMonth ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const cells = useMemo(() => buildCells(cursor), [cursor]);
  const byDay = useMemo(() => groupByDay(tasks), [tasks]);
  const todayKey = dayKey(new Date());
  const cursorMonth = cursor.getMonth();

  function step(delta: number): void {
    setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }

  function gotoToday(): void {
    const d = new Date();
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  return (
    <div
      data-testid="task-calendar"
      className="flex flex-col overflow-hidden rounded-md bg-canvas ring-1 ring-border-subtle"
    >
      <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="前の月"
            onClick={() => step(-1)}
            className="h-8 w-8 rounded-md text-fg-secondary hover:bg-surface-raised"
          >
            ‹
          </button>
          <h2 className="px-2 text-sm font-semibold text-fg-primary">
            {cursor.getFullYear()}年 {cursor.getMonth() + 1}月
          </h2>
          <button
            type="button"
            aria-label="次の月"
            onClick={() => step(1)}
            className="h-8 w-8 rounded-md text-fg-secondary hover:bg-surface-raised"
          >
            ›
          </button>
        </div>
        <button
          type="button"
          onClick={gotoToday}
          className="rounded-md border border-border-default bg-canvas px-3 py-1 text-xs text-fg-secondary hover:bg-surface-raised"
        >
          今日
        </button>
      </header>

      <div className="grid grid-cols-7 border-b border-border-subtle bg-surface-base text-center text-[11px] text-fg-muted">
        {WEEKDAYS_JA.map((d, i) => (
          <div
            key={d}
            className={
              i === 0 ? "py-1.5 text-danger" : i === 6 ? "py-1.5 text-info" : "py-1.5"
            }
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid flex-1 grid-cols-7 auto-rows-fr">
        {cells.map((d) => {
          const key = dayKey(d);
          const list = byDay.get(key) ?? [];
          const isCurrentMonth = d.getMonth() === cursorMonth;
          const isToday = key === todayKey;
          const dow = d.getDay();
          return (
            <div
              key={key}
              className={`min-h-[96px] border-b border-r border-border-subtle p-1.5 ${
                isCurrentMonth ? "bg-canvas" : "bg-surface-base"
              }`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={
                    "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[11px] " +
                    (isToday
                      ? "bg-brand-500 text-fg-inverse"
                      : !isCurrentMonth
                        ? "text-fg-subtle"
                        : dow === 0
                          ? "text-danger"
                          : dow === 6
                            ? "text-info"
                            : "text-fg-secondary")
                  }
                >
                  {d.getDate()}
                </span>
                {list.length > 0 && (
                  <span className="text-[10px] text-fg-muted">{list.length}</span>
                )}
              </div>
              <ul className="space-y-1">
                {list.slice(0, 3).map((task) => (
                  <li key={task.id}>
                    <button
                      type="button"
                      data-testid="calendar-task"
                      onClick={() => onSelect?.(task)}
                      className={`flex w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-left text-[11px] ring-1 ring-inset ${statusChipClass(
                        task.status,
                      )} ${
                        isOverdue(task)
                          ? "ring-danger/40"
                          : "ring-border-subtle"
                      }`}
                      title={`${task.title} · ${STATUS_LABELS_JA[task.status]}`}
                    >
                      <span
                        className={`inline-flex h-3 min-w-[20px] items-center justify-center rounded text-[9px] font-semibold uppercase ${priorityChipClass(
                          task.priority,
                        )}`}
                      >
                        {task.priority}
                      </span>
                      <span className="truncate">{task.title}</span>
                    </button>
                  </li>
                ))}
                {list.length > 3 && (
                  <li className="px-1 text-[10px] text-fg-muted">
                    + {list.length - 3} 件
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function buildCells(monthStart: Date): Date[] {
  // Build a 6×7 grid (42 cells) starting from the Sunday of the first week.
  const first = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth(),
    1,
  );
  const startOffset = first.getDay();
  const result: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(
      first.getFullYear(),
      first.getMonth(),
      i - startOffset + 1,
    );
    result.push(d);
  }
  return result;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function groupByDay(tasks: Task[]): Map<string, Task[]> {
  const out = new Map<string, Task[]>();
  for (const t of tasks) {
    if (!t.due_at) continue;
    const k = dayKey(new Date(t.due_at));
    const arr = out.get(k);
    if (arr) arr.push(t);
    else out.set(k, [t]);
  }
  return out;
}
